import React, { JSX } from 'react';
import ReactDOM from 'react-dom/client';
import html2canvas from 'html2canvas';
import { jsPDF } from 'jspdf';
import { QueryClientProvider } from 'react-query';
import { queryClient } from '../services/react-query/react-query.client';

interface PDFGeneratorOptions {
    margins?: number;
    scale?: number;
    stretch?: number;
    fileName?: string;
    containerWidth?: string; //in pixels
    orientation?: 'portrait' | 'landscape';
}

export class PDFGenerator {
    private container: HTMLElement | null = null;
    private root: ReactDOM.Root | null = null;

    private createContainer() {
        const container = document.createElement('div');
        container.id = 'pdfDownloadContent';

        // Initial styles to hide container
        container.style.cssText = `
      visibility: hidden;
      position: absolute;
      left: -9999px;
      top: -9999px;
      z-index: -999;
    `;

        document.body.appendChild(container);
        return container;
    }

    private async captureBodyStyles(width:string = 'fit-content'): Promise<string> {
        const bodyStyles = window.getComputedStyle(document.body);

        const importantStyles = [
            'font-family',
            'font-size',
            'font-weight',
            'line-height',
            'color',
            'background-color',
            'margin',
            'padding',
            'box-sizing',
            'letter-spacing',
            'text-align',
            'text-transform'
        ];

        const baseStyles = importantStyles
            .map(style => `${style}: ${bodyStyles.getPropertyValue(style)}`)
            .join('; ');


        return `
        ${baseStyles};
        visibility: visible !important;
        position: absolute !important;
        background: white !important;
        width: ${width} !important;
        min-height: 400px !important;
        left: -1600px !important;
        padding: 20px !important;
        z-index: -9999 !important;
    `;
    }


    private async renderContent(content: JSX.Element, containerWidth?: string) {
        if (!this.container) return;

        // Create and inject style element
        this.container.style.cssText = await this.captureBodyStyles(containerWidth);

        // Render React content. This root is detached from the app tree, so
        // it gets none of the app's providers; content using react-query
        // hooks (e.g. the risk assessment result page) needs the shared
        // client, which also serves already-cached data instead of
        // refetching. (.ts file, hence createElement over JSX.)
        this.root = ReactDOM.createRoot(this.container);
        this.root.render(React.createElement(QueryClientProvider, { client: queryClient }, content));

        // Wait for rendering and fonts
        await document.fonts.ready;
        await new Promise(resolve => requestAnimationFrame(resolve));
        // Let in-flight react-query fetches settle (e.g. the offender photo
        // token on a fresh page) so the capture doesn't freeze loading
        // states into the PDF. Bounded: a hung query degrades the export
        // rather than blocking it.
        const settleDeadline = Date.now() + 5000;
        while (queryClient.isFetching() > 0 && Date.now() < settleDeadline) {
            await new Promise(resolve => setTimeout(resolve, 100));
        }
        await new Promise(resolve => setTimeout(resolve, 1000));
    }

    private cleanup() {
        if (this.root) {
            this.root.unmount();
        }
        if (this.container && document.body.contains(this.container)) {
            document.body.removeChild(this.container);
        }
        this.container = null;
        this.root = null;
    }

    /**
     * Y-ranges (in canvas pixels) that a page cut must not land inside.
     *
     * - `pdf-avoid-break` elements must not be sliced through; overlapping
     *   ranges are merged, so a side-by-side row of tiles becomes one band.
     * - `pdf-keep-with-next` elements (section headers) must not be the last
     *   thing on a page: their band extends to the bottom of the first
     *   protected element that follows, so the header moves to the next page
     *   together with its content.
     */
    private collectBreakBands(canvasHeight: number): Array<{ top: number; bottom: number }> {
        if (!this.container || !this.container.scrollHeight) return [];
        const containerTop = this.container.getBoundingClientRect().top;
        const ratio = canvasHeight / this.container.scrollHeight;
        const PAD = 6;            // css px of breathing room around a protected element
        const KEEP_LOOKAHEAD = 120; // css px kept with a header when nothing below is tagged
        const MAX_BIND_GAP = 240;   // css px — don't bind a header across unrelated content

        const rectOf = (el: Element) => {
            const rect = el.getBoundingClientRect();
            return { top: rect.top - containerTop, bottom: rect.bottom - containerTop };
        };
        const avoid = Array.from(this.container.querySelectorAll('.pdf-avoid-break')).map(rectOf);
        const headers = Array.from(this.container.querySelectorAll('.pdf-keep-with-next')).map(rectOf);
        if (!avoid.length && !headers.length) return [];

        headers.forEach((header) => {
            const next = avoid
                .filter((b) => b.top >= header.top && b.top - header.bottom < MAX_BIND_GAP)
                .sort((a, b) => a.top - b.top)[0];
            avoid.push({ top: header.top, bottom: next ? next.bottom : header.bottom + KEEP_LOOKAHEAD });
        });

        const bands = avoid
            .map((band) => ({
                top: (band.top - PAD) * ratio,
                bottom: (band.bottom + PAD) * ratio,
            }))
            .sort((a, b) => a.top - b.top);

        const merged: Array<{ top: number; bottom: number }> = [];
        bands.forEach((band) => {
            const last = merged[merged.length - 1];
            if (last && band.top <= last.bottom) {
                last.bottom = Math.max(last.bottom, band.bottom);
            } else {
                merged.push({ ...band });
            }
        });
        return merged;
    }

    async generatePDF(
        content: JSX.Element,
        options: PDFGeneratorOptions = {}
    ) {
        const {
            margins = 10,
            scale = 2,
            stretch = 1,
            fileName = 'document.pdf',
            orientation = 'portrait',
            containerWidth
        } = options;

        try {
            // Create and setup container
            this.container = this.createContainer();

            // Render content with styles
            await this.renderContent(content, containerWidth);

            if (!this.container) throw new Error('Container not found');

            // Generate canvas
            const canvas = await html2canvas(this.container, {
                scale,
                useCORS: true,
                logging: true,
                backgroundColor: '#FFFFFF',
                width: this.container.scrollWidth,
                height: this.container.scrollHeight,
                windowWidth: this.container.scrollWidth,
                windowHeight: this.container.scrollHeight,
            });

            // Setup PDF dimensions
            const contentWidth = canvas.width;
            const contentHeight = canvas.height;

            // Measure while the container is still mounted: page cuts must not
            // slice through elements marked `pdf-avoid-break`.
            const breakBands = this.collectBreakBands(contentHeight);

            const pageWidth = orientation === 'portrait' ? 210 : 297;  // A4 dimensions
            const pageHeight = orientation === 'portrait' ? 297 : 210;

            const availableWidth = pageWidth - (margins * 2);
            const availableHeight = pageHeight - (margins * 2);

            // Calculate stretched dimensions
            const stretchedWidth = availableWidth * stretch;
            const stretchedHeight = (contentHeight * stretchedWidth) / contentWidth;

            // Create PDF
            const pdf = new jsPDF({
                orientation,
                unit: 'mm',
                format: 'a4'
            });

            const imgData = canvas.toDataURL('image/jpeg', 1.0);

            // Check if content fits on one page
            if (stretchedHeight <= availableHeight) {
                // Single page
                pdf.addImage(
                    imgData,
                    'JPEG',
                    margins,
                    margins,
                    stretchedWidth,
                    stretchedHeight,
                    undefined,
                    'FAST'
                );
            } else {
                // Multi-page - split content
                let yPosition = 0;
                const pageHeightThreshold = availableHeight * 0.95; // 95% of available height

                while (yPosition < stretchedHeight) {
                    // Calculate how much we can fit on current page
                    const remainingHeight = stretchedHeight - yPosition;
                    let pageContentHeight = Math.min(pageHeightThreshold, remainingHeight);

                    // If the cut would land inside a protected element, move it
                    // up to the gap above that element — unless doing so would
                    // leave the page nearly empty (element taller than a page).
                    if (pageContentHeight < remainingHeight && breakBands.length) {
                        const pageTopPx = (yPosition / stretchedHeight) * contentHeight;
                        const cutPx = ((yPosition + pageContentHeight) / stretchedHeight) * contentHeight;
                        const band = breakBands.find((b) => cutPx > b.top && cutPx < b.bottom);
                        if (band) {
                            const minPagePx = pageTopPx + ((pageHeightThreshold * 0.25) / stretchedHeight) * contentHeight;
                            if (band.top > minPagePx) {
                                pageContentHeight = ((band.top - pageTopPx) / contentHeight) * stretchedHeight;
                            }
                        }
                    }

                    // Calculate crop parameters
                    const sourceY = (yPosition / stretchedHeight) * contentHeight;
                    const sourceHeight = (pageContentHeight / stretchedHeight) * contentHeight;

                    // Create temporary canvas for this page's content
                    const tempCanvas = document.createElement('canvas');
                    tempCanvas.width = contentWidth;
                    tempCanvas.height = sourceHeight;
                    const ctx = tempCanvas.getContext('2d');

                    if (ctx) {
                        ctx.drawImage(
                            canvas,
                            0, sourceY, contentWidth, sourceHeight,
                            0, 0, contentWidth, sourceHeight
                        );

                        const pageImgData = tempCanvas.toDataURL('image/jpeg', 1.0);

                        // Add to PDF (add new page if not first page)
                        if (yPosition > 0) {
                            pdf.addPage();
                        }

                        pdf.addImage(
                            pageImgData,
                            'JPEG',
                            margins,
                            margins,
                            stretchedWidth,
                            pageContentHeight,
                            undefined,
                            'FAST'
                        );
                    }

                    yPosition += pageContentHeight;
                }
            }

            pdf.save(fileName);
        } catch (error) {
            console.error('PDF generation failed:', error);
            throw error;
        } finally {
            this.cleanup();
        }
    }
}

export const generatePDF = () => {
    const pdfGenerator = new PDFGenerator();
    const downloadPDF = async (
        content: JSX.Element,
        options?: PDFGeneratorOptions
    ) => {
        try {
            await pdfGenerator.generatePDF(content, options);
        } catch (error) {
            console.error('Failed to generate PDF:', error);
        }
    };

    return { downloadPDF };
};